import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Loader, SelectField } from '@aws-amplify/ui-react';
import { API } from 'aws-amplify';
import { FaBars, FaArrowUp, FaHome, FaArrowLeft } from 'react-icons/fa';
import { RagaSearchBar, RagaAccordion, SongRagaChips } from './RagaLookup';
import { extractSongRagas } from '../utils/ragaMetadata';
import "../js/loader.js";
import "../js/appConstants.js";

const languageCodeMap = {
  'ENG': 'English', 'TAM': 'Tamil', 'IAST': 'English (IAST)',
  'SAN': 'Sanskrit', 'MIX': 'Mixed', 'DEV': 'Devanagari', 'ROM': 'Roman'
};

const formatFileName = (fn) => fn.replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, '');

function FileViewer() {
  const { folder, language, fileName } = useParams();
  const navigate = useNavigate();
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translationType, setTranslationType] = useState('');
  const [translatedContent, setTranslatedContent] = useState('');
  const [translating, setTranslating] = useState(false);
  const [otherLanguages, setOtherLanguages] = useState([]);
  const [showLangPopover, setShowLangPopover] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectedMeaning, setSelectedMeaning] = useState('');
  const [processingSelected, setProcessingSelected] = useState(false);
  const [actionBarPosition, setActionBarPosition] = useState(null);
  const [isContentAvailable, setIsContentAvailable] = useState(true);
  const [songNo, setSongNo] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedRaga, setSelectedRaga] = useState(null);
  const songRagas = React.useMemo(() => extractSongRagas(fileContent), [fileContent]);
  const fileContentRef = React.useRef();
  const hamburgerRef = React.useRef();

  useEffect(() => {
    const handleLoad = () => window.clickLog('load', 'fileviewer');
    if (document.readyState === 'complete') handleLoad();
    else window.addEventListener('load', handleLoad);
    return () => window.removeEventListener('load', handleLoad);
  }, []);

  useEffect(() => {
    fetchFileContent();
    setTranslationType('');
    setTranslatedContent('');
    setSelectedMeaning('');
    setSelectedRaga(null);
  }, [folder, language, fileName]);

  useEffect(() => {
    const fetchOtherLanguages = async () => {
      try {
        const res = await API.get('NamapagesAPI', 'files', { headers: { 'Accept': 'application/json' } });
        const allFiles = Array.isArray(res) ? res : (res?.files || []);
        const baseName = formatFileName(decodeURIComponent(fileName));
        setOtherLanguages(allFiles.filter(f => formatFileName(f.file_name) === baseName));
      } catch { setOtherLanguages([]); }
    };
    fetchOtherLanguages();
  }, [fileName]);

  useEffect(() => {
    const handleScrollOrClick = () => { setActionBarPosition(null); setSelectedText(''); };
    window.addEventListener('scroll', handleScrollOrClick);
    window.addEventListener('mousedown', handleScrollOrClick);
    return () => { window.removeEventListener('scroll', handleScrollOrClick); window.removeEventListener('mousedown', handleScrollOrClick); };
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchFileContent = async () => {
    setLoading(true);
    setError(null);
    try {
      const decodedFileName = decodeURIComponent(fileName);
      const fileListResponse = await API.get('NamapagesAPI', 'files', { headers: { 'Accept': 'application/json' } });
      let fileData = null;
      if (Array.isArray(fileListResponse)) {
        fileData = fileListResponse.find(f => f.folder === folder && f.language === language && f.file_name === decodedFileName);
      } else if (fileListResponse?.files) {
        fileData = fileListResponse.files.find(f => f.folder === folder && f.language === language && f.file_name === decodedFileName);
      }
      if (fileData?.song_no) setSongNo(fileData.song_no);

      let response;
      if (fileData?.s3_path) {
        response = await API.post('NamapagesAPI', 'file', { body: { s3_path: fileData.s3_path } });
      } else {
        response = await API.post('NamapagesAPI', 'file', { body: { file_name: decodedFileName, language: `${folder}/${language}` } });
      }
      setIsContentAvailable(typeof response === 'string' && !response.trim().startsWith('NOT AVAILABLE IN'));
      setFileContent(response);
    } catch (err) {
      setError('Failed to fetch file content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslate = async (selectedType) => {
    const typeToUse = selectedType || translationType;
    if (!typeToUse || !fileContent) return;
    setTranslating(true);
    try {
      const response = await API.post('NamapagesAPI', 'translate', { body: { text: fileContent, translationType: typeToUse } });
      if (response?.output) setTranslatedContent(response.output);
      else setError('Translation failed. Invalid response format.');
    } catch { setError('Failed to translate content. Please try again later.'); }
    finally { setTranslating(false); }
    window.clickLog('Translating with type: ' + typeToUse, 'fileviewer');
  };

  const handleMouseUp = (e) => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (text && fileContentRef.current?.contains(selection.anchorNode)) {
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      setActionBarPosition({ x: rect.left + window.scrollX + rect.width / 2, y: rect.top + window.scrollY - 48 });
      setSelectedText(text);
    } else {
      setActionBarPosition(null);
      setSelectedText('');
    }
  };

  const handleSelectedTranslate = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!selectedText) return;
    setProcessingSelected(true);
    setSelectedMeaning('');
    setError(null);
    try {
      const response = await API.post('NamapagesAPI', 'translate', { body: { text: selectedText, translationType: 'provide_sanskrit_meaning' } });
      if (response?.output) {
        setSelectedMeaning(response.output);
        setActionBarPosition(null);
        setSelectedText('');
      } else setError('Meaning fetch failed.');
    } catch { setError('Failed to fetch meaning. Please try again later.'); }
    finally { setProcessingSelected(false); if (window.getSelection) window.getSelection().removeAllRanges(); }
    window.clickLog('Selected text meaning: ' + selectedText, 'fileviewer');
  };

  // Compute language display label
  let languageDisplay = languageCodeMap[language] || language?.charAt(0).toUpperCase() + language?.slice(1) || '';

  if (loading) {
    return (
      <div className="np-loading-wrap">
        <Loader size="large" />
        <p className="np-loading-text">🪷 Loading sacred content…</p>
      </div>
    );
  }

  if (error && !fileContent) {
    return (
      <div className="np-error-box" style={{ margin: '2rem auto', maxWidth: 500 }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🙏</div>
        <p>{error}</p>
        <Link to="/" className="np-btn" style={{ marginTop: '1rem', display: 'inline-flex' }}>⬅ Back to Home</Link>
      </div>
    );
  }

  return (
    <div>
      {/* ── Top nav bar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '0.75rem', flexWrap: 'wrap' }}>

        {/* Translate + Raga search — same row, left side */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap' }}>
            <SelectField
              label=""
              value={translationType}
              onChange={(e) => {
                const v = e.target.value;
                setTranslationType(v);
                if (v) handleTranslate(v);
              }}
              isDisabled={!isContentAvailable || translating}
              style={{
                width: 160, fontSize: '0.82rem', borderRadius: 'var(--radius-pill)',
                borderColor: 'var(--gold-mid)', background: 'var(--parchment)',
                color: 'var(--text-primary)', fontFamily: 'var(--font-heading)'
              }}
            >
              <option value="">🔮 Translate</option>
              {language !== 'SAN' && <option value="convert_to_sanskrit">→ Sanskrit</option>}
              <option value="convert_from_sanskrit">→ English</option>
              <option value="provide_sanskrit_meaning">📖 Meaning</option>
            </SelectField>

            {/* Raga search — same row as Translate */}
            <RagaSearchBar onRagaSelect={setSelectedRaga} inputWidth={200} />
          </div>

          {translating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: 14, height: 14, border: '2px solid var(--gold-light)',
                borderTop: '2px solid var(--gold-deep)', borderRadius: '50%',
                animation: 'np-spin 0.9s linear infinite'
              }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Processing…</span>
            </div>
          )}
          <span style={{ fontSize: '0.65rem', color: 'var(--saffron-dark)', fontStyle: 'italic', maxWidth: 380, lineHeight: 1.3 }}>
            AI translation for learning only. May not be fully accurate.
          </span>
          <style>{`@keyframes np-spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        </div>

        {/* Back + Home buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: 'auto' }}>
          <button
            className="np-btn np-btn-saffron np-btn-icon"
            onClick={() => navigate('/', { state: { backFromFile: true, folder, language, fileName: decodeURIComponent(fileName) } })}
            title="Back"
          >
            <FaArrowLeft />
          </button>
          <Link to="/" className="np-btn np-btn-icon" style={{ background: 'linear-gradient(135deg, var(--maroon), var(--brown-mid))', borderColor: 'var(--gold-mid)' }} title="Home">
            <FaHome style={{ color: 'var(--gold-light)' }} />
          </Link>
        </div>
      </div>

      {/* ── Raga accordion — above the song title card ── */}
      <RagaAccordion raga={selectedRaga} />

      {/* ── Song title card ── */}
      <div className="np-panel" style={{ marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontFamily: 'var(--font-heading)', fontSize: '1.05rem', color: 'var(--maroon-mid)', letterSpacing: '0.5px' }}>
              {decodeURIComponent(fileName).replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, '')}
            </h2>
            {songNo && (
              <span className="np-song-badge" style={{ fontSize: '0.68rem', padding: '2px 10px' }}>
                Song #{songNo}
              </span>
            )}
          </div>
          <div style={{
            display: 'flex', gap: '6px', alignItems: 'center',
            background: 'linear-gradient(135deg, #F0C040, #E8A800)',
            padding: '4px 12px', borderRadius: 'var(--radius-pill)',
            border: '1.5px solid var(--gold-deep)',
            boxShadow: '0 1px 6px rgba(184,134,11,0.25)'
          }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '0.7rem', color: 'var(--brown-deep)', letterSpacing: '0.5px', fontWeight: 700 }}>
              {folder.charAt(0).toUpperCase() + folder.slice(1).replace(/_/g, ' ')}
            </span>
            <span style={{ color: 'var(--brown-mid)', opacity: 0.5 }}>/</span>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '0.7rem', color: 'var(--brown-deep)', letterSpacing: '0.5px', fontWeight: 700 }}>
              {languageDisplay}
            </span>
          </div>
        </div>

        {/* Language switcher (hamburger) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.65rem', position: 'relative' }}>
          <button
            ref={hamburgerRef}
            className="np-btn np-btn-icon"
            onClick={() => setShowLangPopover(v => !v)}
            title="Other Languages"
            style={{ background: 'linear-gradient(135deg, #1565C0, #1976D2)', borderColor: 'rgba(25,118,210,0.4)' }}
          >
            <FaBars size={14} />
          </button>

          {showLangPopover && (
            <div className="np-popover" style={{ position: 'absolute', top: '110%', right: 0, zIndex: 1000, minWidth: 270 }}>
              <div className="np-popover-header">🌐 Other Languages</div>
              {otherLanguages.length === 0 ? (
                <div style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-body)', fontSize: '0.88rem' }}>
                  No other languages found
                </div>
              ) : otherLanguages.map(variant => {
                const langLabel = languageCodeMap[variant.language] || variant.language;
                const display = `${langLabel}: ${variant.song_no} — ${formatFileName(variant.file_name)}`;
                const isCurrent = variant.folder === folder && variant.language === language && variant.file_name === decodeURIComponent(fileName);
                const to = `/file/${variant.folder}/${variant.language}/${encodeURIComponent(variant.file_name)}`;
                return isCurrent ? (
                  <div key={variant.folder + variant.language + variant.file_name} className="np-popover-item np-popover-item-active">
                    ✓ {display}
                  </div>
                ) : (
                  <Link key={variant.folder + variant.language + variant.file_name} to={to} style={{ textDecoration: 'none' }} onClick={() => setShowLangPopover(false)}>
                    <div className="np-popover-item">{display}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <SongRagaChips ragas={songRagas} onRagaSelect={setSelectedRaga} />

      <hr className="np-divider-thin" />

      {/* ── Song Content ── */}
      <div
        ref={fileContentRef}
        className="np-content-area"
        style={{ maxHeight: translatedContent ? '38vh' : '68vh', position: 'relative' }}
        onMouseUp={handleMouseUp}
        onContextMenu={e => e.preventDefault()}
      >
        {fileContent}
      </div>

      {/* Floating "Sanskrit Meaning" action bar */}
      {selectedText && actionBarPosition && (
        <div
          style={{
            position: 'absolute', left: actionBarPosition.x, top: actionBarPosition.y,
            zIndex: 2000, transform: 'translate(-50%, 0)',
            background: 'linear-gradient(135deg, var(--brown-deep), var(--maroon))',
            border: '1px solid var(--gold-mid)', color: 'var(--gold-light)',
            borderRadius: 'var(--radius-pill)', padding: '0.35rem 1.1rem',
            boxShadow: '0 4px 18px rgba(62,31,0,0.35)', cursor: 'pointer',
            fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.82rem',
            display: 'flex', alignItems: 'center', gap: '0.5rem', userSelect: 'none', pointerEvents: 'auto',
            letterSpacing: '0.5px'
          }}
          role="button"
          tabIndex={0}
          onMouseDown={handleSelectedTranslate}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSelectedTranslate(e); }}
          title="Get Sanskrit meaning"
        >
          {processingSelected ? (
            <>
              <span style={{
                display: 'inline-block', width: 14, height: 14,
                border: '2px solid var(--gold-light)', borderTop: '2px solid transparent',
                borderRadius: '50%', animation: 'np-spin 0.9s linear infinite'
              }} />
              Processing…
            </>
          ) : (
            <><span>🔍</span> Sanskrit Meaning</>
          )}
        </div>
      )}

      {processingSelected && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '0.75rem' }}>
          <div style={{ width: 16, height: 16, border: '2px solid var(--border-gold)', borderTop: '2px solid var(--gold-deep)', borderRadius: '50%', animation: 'np-spin 0.9s linear infinite' }} />
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Fetching meaning…</span>
        </div>
      )}

      {/* Translation result */}
      {translatedContent && !translating && (
        <>
          <hr className="np-divider" />
          <h4 style={{ fontFamily: 'var(--font-heading)', color: 'var(--maroon-mid)', letterSpacing: '1px', margin: '0 0 0.75rem', fontSize: '0.85rem', textTransform: 'uppercase' }}>
            🔮 Translation Result
          </h4>
          <div className="np-content-area np-content-translated" style={{ maxHeight: '38vh' }}>
            {translatedContent}
          </div>
        </>
      )}

      {/* Selected-text meaning result */}
      {selectedMeaning && !processingSelected && (
        <>
          <hr className="np-divider" />
          <h4 style={{ fontFamily: 'var(--font-heading)', color: 'var(--maroon-mid)', letterSpacing: '1px', margin: '0 0 0.75rem', fontSize: '0.85rem', textTransform: 'uppercase' }}>
            📖 Meaning for Selected Text
          </h4>
          <div className="np-content-area np-content-translated" style={{ maxHeight: '38vh' }}>
            {selectedMeaning}
          </div>
        </>
      )}

      {/* Error inline */}
      {error && fileContent && (
        <div className="np-error-box" style={{ marginTop: '1rem' }}>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Scroll to top */}
      {showScrollTop && (
        <button className="np-scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Scroll to top">
          <FaArrowUp />
        </button>
      )}
    </div>
  );
}

export default FileViewer;
